import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from "react";

import { useInView } from "@/hooks/use-in-view";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/utils";

type RevealVariant = "up" | "left" | "right" | "scale" | "fade" | "image";

type RevealProps = {
  children: ReactNode;
  className?: string;
  variant?: RevealVariant;
  delay?: number;
  duration?: number;
  as?: ElementType;
  once?: boolean;
  style?: CSSProperties;
};

export function Reveal({
  children,
  className,
  variant = "up",
  delay = 0,
  duration = 900,
  as: Comp = "div",
  once = true,
  style,
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const inView = useInView(ref, { once });
  const reduced = usePrefersReducedMotion();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  const active = !ready || reduced || inView;

  return (
    <Comp
      ref={ref as never}
      className={cn("reveal", `reveal-${variant}`, active && "is-revealed", className)}
      style={
        {
          "--reveal-delay": `${delay}ms`,
          "--reveal-duration": `${duration}ms`,
          ...style,
        } as CSSProperties
      }
    >
      {children}
    </Comp>
  );
}
